// @flow
import { array, object, string } from 'yup';
import { getIn } from 'formik';
import { cloneDeep, mapValues } from 'lodash';

import {
  CLUSTER_CLUSTER_LINKING_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_PROVIDER_BACKEND_NAME,
  CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_RTCE_STORAGE_TYPE_BACKEND_NAME,
  CLUSTER_TABLEFLOW_STORAGE_TYPE_BACKEND_NAME,
  CONNECTOR_DESTINATION_PROVIDER_BACKEND_NAME,
  CONNECTOR_DESTINATION_PROVIDER_DISPLAY_NAME,
  CONNECTOR_DESTINATION_REGION_BACKEND_NAME,
  CONNECTOR_DESTINATION_REGION_DISPLAY_NAME,
  DEFAULT_VALUE_FOR_DB_ROW_ID_STR,
  IS_CONNECT_CROSS_CLOUD_REGION_LIVE,
  IS_CONNECT_SMT_TYPE_LIVE,
} from '../../../constants';
import { getMonthlyInputsValidationFuncForType } from '../cluster-details-inputs/kafka-monthly-inputs-validation';
import { getDestinationDropdownsVisibility } from '../cluster-monthly-inputs/utils';
import { TABLEFLOW_WIRE_KEY_BY_VERSION } from '../cluster-monthly-inputs/tableflow-config';
import { toastError } from '../../presentational/notifications/utils';

export const getMonthName = (i) => {
  return isNaN(i) ? i : `Month ${i}`;
};
export const getYearName = (i) => `Year ${i}`;

// Every toggle row (Active, Automatic Failover, ...) is locked in a month whose Kafka is disabled
export const isMonthLockedByKafkaMonth = (
  formikValues,
  monthName,
  shouldDisableMonthWhenCorrespondingKafkaMonthIsDisabled
) =>
  shouldDisableMonthWhenCorrespondingKafkaMonthIsDisabled &&
  !getIn(
    formikValues,
    `${CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG}[months][${monthName}].is_enabled`
  );

export const getStartAndEndMonthsForGivenYear = (selectedYear) => {
  const startMonthNumber = 12 * (selectedYear - 1) + 1;
  const endMonthNumber = 12 * (selectedYear - 1) + 12;
  return { startMonthNumber, endMonthNumber };
};

export const getRelevantMonthsBasedOnSelectedYear = (selectedYear, allMonths) => {
  const monthsToRet = [];
  const { startMonthNumber, endMonthNumber } = getStartAndEndMonthsForGivenYear(selectedYear);

  for (let i = startMonthNumber; i <= endMonthNumber; i++) {
    if (Object.prototype.hasOwnProperty.call(allMonths, getMonthName(i))) {
      monthsToRet.push(allMonths[getMonthName(i)]);
    }
  }

  return monthsToRet;
};

export const addAdditionalDummyMonthsIfRequired = (selectedYear, months) => {
  const { startMonthNumber, endMonthNumber } = getStartAndEndMonthsForGivenYear(selectedYear);
  const monthNamesPassed = months.map((x) => x.name);
  const monthsToUse = [...months];

  for (let i = startMonthNumber; i <= endMonthNumber; i++) {
    if (!monthNamesPassed.includes(getMonthName(i))) {
      monthsToUse.push({
        name: getMonthName(i),
        isMonthArtificiallyGenerated: true,
      });
    }
  }
  return monthsToUse;
};

export const getDownloadColumns = (dataInFormik) => {
  const downloadColumns = [{ id: 'Input', name: 'Input' }];
  for (const [monthName] of Object.entries(dataInFormik)) {
    downloadColumns.push({
      id: monthName,
      name: monthName,
    });
  }
  return downloadColumns;
};

export const getDownloadData = (
  rowsConfig,
  dataInFormik,
  values,
  contextData,
  addCalendarMonthRowToDownload = false
) => {
  const dataToDownload = [];
  const rowsConfigToUse = [...rowsConfig];
  if (addCalendarMonthRowToDownload) {
    rowsConfigToUse.unshift({
      displayName: 'Calendar Month',
      backendName: 'calendar_month',
    });
  }
  for (const row of rowsConfigToUse) {
    const shouldDownloadRow = !row.showOrHideFunc ? true : row.showOrHideFunc(values, contextData);
    if (!shouldDownloadRow) {
      continue;
    }
    const currRowObj = {};
    // Use displayNameForDownloads if available, otherwise fall back to displayName
    currRowObj.Input = row.displayNameForDownloads || row.displayName;
    // Month-wise Data
    for (const [monthName, monthData] of Object.entries(dataInFormik)) {
      currRowObj[monthName] = monthData[row.backendName];
    }

    dataToDownload.push(currRowObj);
  }

  return dataToDownload;
};

export const updateClusterMonthlyInputsSubmitHandler = async (
  estimateId,
  clusterId,
  values,
  updateFunc
) => {
  const payload = {
    values,
  };

  const { error } = await updateFunc({
    estimateId: estimateId,
    clusterId: clusterId,
    payload,
  });

  if (error) {
    toastError(error);
  }
};

export const clusterCalculationsDataInputTransformationFunc = (originalData) => {
  // First, call the general Monthly Input transformation func
  const toRet = clusterMonthlyDataInputTransformationFunc(originalData);

  for (const monthName of Object.keys(toRet.months)) {
    const existingMonthlyObject = toRet.months[monthName];

    // Transform the monthly object based on FE needs
    toRet.months[monthName] = mapValues(existingMonthlyObject, (value, key) => {
      // For these two guys, there is no transformation required
      if (['month', 'name'].includes(key)) {
        return value;
      }

      // For all others, we are going to get a string, first, we will need to convert it into a Number and then apply commas using toLocaleString
      return Number(value).toLocaleString();
    });
  }

  return toRet;
};

export const clusterMonthlyDataInputTransformationFunc = (originalData) => {
  // Formik now seeds only the active Tableflow version's key, so the inactive one's config
  // entry (needed for validation registration) is transformed from undefined.
  const monthlies = originalData?.inputs ?? [];
  const monthlyInputsTransformed = {};
  for (let monthData of monthlies) {
    monthData = {
      ...monthData,
      name: getMonthName(monthData.month),
    };
    monthlyInputsTransformed[getMonthName(monthData.month)] = monthData;
  }
  return { months: monthlyInputsTransformed };
};

export const tableflowMonthlyDataInputTransformationFunc = (originalData) => {
  const monthlyInputsTransformed = clusterMonthlyDataInputTransformationFunc(originalData);
  // Add storage_type to the transformed data
  return {
    ...monthlyInputsTransformed,
    storage_type: originalData?.storage_type,
    // TODO::CE1 - Use constant defined for storage_type
  };
};

export const rtceMonthlyDataInputTransformationFunc = (originalData = {}) => {
  const monthlyInputsTransformed = clusterMonthlyDataInputTransformationFunc({
    inputs: originalData.inputs ?? [],
  });
  return {
    ...monthlyInputsTransformed,
    [CLUSTER_RTCE_STORAGE_TYPE_BACKEND_NAME]: originalData.storage_type,
  };
};

/**
 * Extracts the destination fields from the connector payload into flat formik fields.
 *
 * Destination Cloud Provider and Region apply only to sink connectors on connect public networking,
 * and Region applies only when the destination is on the same cloud provider as the cluster.
 * Hence, default both fields to null when they are not present on the payload.
 */
const extractDestinationFieldsFromConnectorInputDetails = (inputDetails) => ({
  [CONNECTOR_DESTINATION_PROVIDER_BACKEND_NAME]: inputDetails.destination?.cloud_provider ?? null,
  [CONNECTOR_DESTINATION_REGION_BACKEND_NAME]: inputDetails.destination?.region ?? null,
});

export const connectorsMonthlyDataInputTransformationFunc = (originalData) => {
  const monthlyInputsTransformed = {};
  const connectorsTransformedDataArray = [];
  const dataToUse = originalData.connectors ? originalData.connectors : [];

  for (const connector of dataToUse) {
    connectorsTransformedDataArray.push({
      connectorName: connector.name,
      connectorNetworkingType: connector.input_details.networking_type,
      ...(IS_CONNECT_SMT_TYPE_LIVE && {
        connectorSmtType: connector.input_details.smt_type,
      }),
      ...(IS_CONNECT_CROSS_CLOUD_REGION_LIVE &&
        extractDestinationFieldsFromConnectorInputDetails(connector.input_details)),
      connectorId: connector.connector_id,
      connectorLabel: connector.label,
      months: clusterMonthlyDataInputTransformationFunc(connector).months,
    });
  }
  monthlyInputsTransformed.connectors = connectorsTransformedDataArray;
  return monthlyInputsTransformed;
};

const getConnectorMetadataInputValidationFuncForGivenField =
  (fieldName, fieldLabel, connectorsList) =>
  (value, { createError, path, from }) => {
    const formValues = from[2].value;
    const currentLevelValue = from[0]?.value;
    const validOptions = connectorsList.filter(
      (x) =>
        x.cluster_type === formValues.cluster_type &&
        x.connector_name === currentLevelValue?.connectorName &&
        x.provider === formValues.provider &&
        x[fieldName] === value
    );
    const isError = validOptions.length === 0;
    if (isError) {
      return createError({
        message: `The ${fieldLabel} chosen is not valid for the chosen configuration of cluster! Please delete this cluster!`,
        path: path,
      });
    }
    return true;
  };

/**
 * Validation for the destination fields i.e., provider and region.
 *
 * These fields are required only when they are shown in the UI. So if the field is not shown, it is
 * always valid; if it is shown, it must have a value.
 *
 * fieldVisibilityKey will have the value of either showDestinationProvider or showDestinationRegion.
 */
const getDestinationFieldRequiredValidationFunc =
  (cluster, fieldVisibilityKey) =>
  (value, { from }) => {
    const connector = from[0].value;
    const {
      connectorName,
      connectorNetworkingType,
      [CONNECTOR_DESTINATION_PROVIDER_BACKEND_NAME]: connectorDestinationProvider,
    } = connector;

    const { [fieldVisibilityKey]: isFieldVisible } = getDestinationDropdownsVisibility({
      connectorName,
      networkingType: connectorNetworkingType,
      destinationProvider: connectorDestinationProvider,
      clusterProvider: cluster?.inputs?.[CLUSTER_PROVIDER_BACKEND_NAME],
    });

    if (!isFieldVisible) {
      return true;
    }
    return Boolean(value);
  };

export const connectorsMonthlyInputsValidationFunc = ({ estimate, cluster, metaData }) => {
  const monthsSchema = getMonthlyInputsValidationFuncForType(
    CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG
  )({
    estimate,
    metaData,
  });

  const connectorsList = cluster?.cluster_configs?.full_connectors_list.connector_types;

  const connectorSchemaObject = monthsSchema.concat(
    object({
      connectorName: string()
        .label('Connector Name')
        .required()
        .test(
          'connectorNotFound',
          'This connector is not valid for the chosen configuration of cluster! Please delete!',
          (value, { createError, path, from }) => {
            const formValues = from[2].value;
            const validOptions = connectorsList.filter(
              (x) =>
                x.cluster_type === formValues.cluster_type &&
                x.networking_type === formValues.networking_type &&
                x.provider === formValues.provider &&
                x.connector_name === value
            );

            const isError = validOptions.length === 0;
            if (isError) {
              return createError({
                message: `This connector is not valid for the chosen configuration of cluster! Please delete!`,
                path: path,
              });
            }
            return true;
          }
        ),
      connectorLabel: string().required().label('Connector Label').nullable(),
      connectorNetworkingType: string()
        .required('Connector Networking Type is required, please add!')
        .label('Connector Networking Type')
        .test(
          'connectorNetworkingTypeNotFound',
          'This Networking Type is not valid for the chosen configuration of cluster! Please delete!',
          getConnectorMetadataInputValidationFuncForGivenField(
            'networking_type',
            'Connector Networking Type',
            connectorsList
          )
        ),
      ...(IS_CONNECT_SMT_TYPE_LIVE && {
        connectorSmtType: string()
          .required('Connector SMT Type is required, please add!')
          .label('Connector SMT Type')
          .test(
            'connectorSmtTypeNotFound',
            'This SMT Type is not valid for the chosen configuration of cluster! Please delete!',
            getConnectorMetadataInputValidationFuncForGivenField(
              'smt_type',
              'Connector SMT Type',
              connectorsList
            )
          ),
      }),
      ...(IS_CONNECT_CROSS_CLOUD_REGION_LIVE && {
        [CONNECTOR_DESTINATION_PROVIDER_BACKEND_NAME]: string()
          .nullable()
          .label(CONNECTOR_DESTINATION_PROVIDER_DISPLAY_NAME)
          .test(
            'destinationProviderRequired',
            `${CONNECTOR_DESTINATION_PROVIDER_DISPLAY_NAME} is required, please add!`,
            getDestinationFieldRequiredValidationFunc(cluster, 'showDestinationProvider')
          ),
        [CONNECTOR_DESTINATION_REGION_BACKEND_NAME]: string()
          .nullable()
          .label(CONNECTOR_DESTINATION_REGION_DISPLAY_NAME)
          .test(
            'destinationRegionRequired',
            `${CONNECTOR_DESTINATION_REGION_DISPLAY_NAME} is required, please add!`,
            getDestinationFieldRequiredValidationFunc(cluster, 'showDestinationRegion')
          ),
      }),
    })
  );

  return object({
    connectors: array(connectorSchemaObject).notRequired().default([]),
  });
};

export const getKafkaNewlyDisabledMonths = (kafkaMonthsData, initialValues, values) => {
  const disabledMonths = [];

  for (const [monthName] of Object.entries(kafkaMonthsData)) {
    const kafkaMonthFieldName = `${CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG}[months][${monthName}].is_enabled`;
    const originalEnabledForMonth = getIn(initialValues, kafkaMonthFieldName);
    const currentEnabledForMonth = getIn(values, kafkaMonthFieldName);
    if (originalEnabledForMonth !== currentEnabledForMonth && currentEnabledForMonth === false) {
      disabledMonths.push(monthName);
    }
  }

  return disabledMonths;
};

/**
 * Builds the nested destination struct sent to the backend for a connector.
 *
 * The struct is always sent; cloud_provider and region are null when not chosen.
 */
const prepareDestinationInputDetailsFromConnectorValue = (connectorValues) => ({
  destination: {
    cloud_provider: connectorValues[CONNECTOR_DESTINATION_PROVIDER_BACKEND_NAME] ?? null,
    region: connectorValues[CONNECTOR_DESTINATION_REGION_BACKEND_NAME] ?? null,
  },
});

export const getConnectorInputsFromConnectorValue = (connectorValues) => ({
  connector_id:
    connectorValues.connectorId === DEFAULT_VALUE_FOR_DB_ROW_ID_STR
      ? undefined
      : connectorValues.connectorId,
  name: connectorValues.connectorName,
  input_details: {
    networking_type: connectorValues.connectorNetworkingType,
    ...(IS_CONNECT_SMT_TYPE_LIVE && {
      smt_type: connectorValues.connectorSmtType,
    }),
    ...(IS_CONNECT_CROSS_CLOUD_REGION_LIVE &&
      prepareDestinationInputDetailsFromConnectorValue(connectorValues)),
  },
  label: connectorValues.connectorLabel,
  inputs: Object.values(connectorValues.months),
});

// tableflowVersion is required, not defaulted: the backend 400s on the wrong object, and each
// version's Formik state lives under its own key too, so the wrong version silently drops data.
export const getDataForOtherTabsThatNeedToBeSentToBackEnd = (
  values,
  disabledMonths,
  tableflowVersion
) => {
  const tableflowKey = TABLEFLOW_WIRE_KEY_BY_VERSION[tableflowVersion];
  if (tableflowKey === undefined) {
    throw new Error(`Unknown Tableflow version: ${String(tableflowVersion)}`);
  }

  const ksqlDBValues = cloneDeep(getIn(values, CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG));
  const clusterLinkingValues = cloneDeep(
    getIn(values, CLUSTER_CLUSTER_LINKING_MONTHLY_INPUTS_JSON_CONFIG)
  );
  const connectorValues = cloneDeep(
    getIn(values, CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG).connectors
  );
  const tableflowValues = cloneDeep(getIn(values, tableflowKey));
  const rtceValues = cloneDeep(getIn(values, CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG));

  for (const month of disabledMonths) {
    ksqlDBValues.months[month].is_enabled = false;

    if (clusterLinkingValues !== undefined) {
      clusterLinkingValues.months[month].is_enabled = false;
      // Switch failover off only if it's on, so clusters that never had the field don't start
      // sending it
      if (clusterLinkingValues.months[month].is_automatic_failover_enabled) {
        clusterLinkingValues.months[month].is_automatic_failover_enabled = false;
      }
    }
    for (const connector of connectorValues) {
      connector.months[month].is_enabled = false;
    }
    if (tableflowValues !== undefined) {
      tableflowValues.months[month].is_enabled = false;
    }
    if (rtceValues !== undefined) {
      rtceValues.months[month].is_enabled = false;
    }
  }

  const toRet = {};

  toRet[CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG] = { inputs: Object.values(ksqlDBValues.months) };

  if (clusterLinkingValues !== undefined) {
    toRet[CLUSTER_CLUSTER_LINKING_MONTHLY_INPUTS_JSON_CONFIG] = {
      inputs: Object.values(clusterLinkingValues.months),
    };
  }

  toRet[CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG] = connectorValues.map((connectorElem) =>
    getConnectorInputsFromConnectorValue(connectorElem)
  );
  if (tableflowValues !== undefined) {
    toRet[tableflowKey] = {
      inputs: Object.values(tableflowValues.months),
      [CLUSTER_TABLEFLOW_STORAGE_TYPE_BACKEND_NAME]:
        tableflowValues[CLUSTER_TABLEFLOW_STORAGE_TYPE_BACKEND_NAME],
    };
  }

  if (rtceValues !== undefined) {
    toRet[CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG] = {
      inputs: Object.values(rtceValues.months),
      [CLUSTER_RTCE_STORAGE_TYPE_BACKEND_NAME]: rtceValues[CLUSTER_RTCE_STORAGE_TYPE_BACKEND_NAME],
    };
  }

  return toRet;
};
